import { iconClasses, styled, Theme, useThemeProps } from '@mui/material';
import {
  default as MuiAvatar,
  AvatarTypeMap as MuiAvatarTypeMap,
} from '@mui/material/Avatar';
import { OverrideProps } from '@mui/material/OverridableComponent';
import * as React from 'react';
import {
  ExtendTypeMap,
  forwardRefToOverridableComponent,
} from 'src/utils/forwardRefToOverridableComponent';
import getAvatarColor, { colorNameSymbol } from './getAvatarColor';
import clsx from 'clsx';
import { getAvatarUtilityClass } from './avatarClasses';

export function getHeightAndWidth(size: string, theme: Theme): object {
  return {
    ...(size === 'xsmall' && {
      ...theme.typography.caption,
      height: '24px',
      width: '24px',
      [`& > .${iconClasses.root}`]: {
        fontSize: '20px',
      },
    }),
    ...(size === 'small' && {
      ...theme.typography.body1,
      height: '40px',
      width: '40px',
    }),
    ...(size === 'medium' && {
      ...theme.typography.body1,
      height: '48px',
      width: '48px',
    }),
    ...(size === 'large' && {
      ...theme.typography.h5,
      fontWeight: theme.typography.fontWeightRegular,
      height: '56px',
      width: '56px',
    }),
    ...(size === 'xlarge' && {
      ...theme.typography.h4,
      fontWeight: theme.typography.fontWeightRegular,
      height: '64px',
      width: '64px',
    }),
  };
}

export interface AvatarOwnProps {
  /**
   * The size of the component.
   * @default 'xsmall'
   */
  size?: 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';

  /**
   * An arbitrary string that, if specified, will be used to automatically select a color to use for the Avatar's background and text.
   *
   * The specific color chosen will always be the same for equal strings, but that color may change across different versions of this library.
   *
   * For instance, when rendering an Avatar that represents a user, this can be set to the user's ID.
   *
   * If this prop is not set, the Avatar will stay the default color.
   */
  colorId?: string;
}

export type AvatarTypeMap<
  AdditionalProps = unknown,
  RootComponent extends React.ElementType = MuiAvatarTypeMap['defaultComponent']
> = ExtendTypeMap<
  MuiAvatarTypeMap,
  AdditionalProps & AvatarOwnProps,
  RootComponent
>;

export type AvatarProps<
  RootComponent extends React.ElementType = MuiAvatarTypeMap['defaultComponent'],
  AdditionalProps = unknown
> = OverrideProps<AvatarTypeMap<AdditionalProps, RootComponent>, RootComponent>;

/**
 * An avatar is a visual representation of an entity or customer.
 *
 * Demos:
 *
 * - [Avatar](https://unify.workiva.org/components/avatar?tab=code)
 *
 * API:
 *
 * - [Avatar API](https://unify.workiva.org/components/avatar?tab=props)
 */
export const Avatar = forwardRefToOverridableComponent<
  AvatarTypeMap,
  AvatarProps
>(function Avatar(inProps, forwardedRef) {
  const props = useThemeProps({ props: inProps, name: 'MuiAvatar' });
  const { className, colorId, ...rest } = props;
  const avatarColor = colorId
    ? getAvatarColor(colorId)[colorNameSymbol]
    : undefined;

  const classes = clsx(
    avatarColor && getAvatarUtilityClass(avatarColor),
    className
  );

  return (
    <AvatarStyled
      ref={forwardedRef}
      colorId={colorId}
      className={classes}
      {...rest}
    ></AvatarStyled>
  );
});

export const AvatarStyled = styled(MuiAvatar, {
  shouldForwardProp: (prop) => prop !== 'size' && prop !== 'colorId',
})<AvatarProps>(({ theme, size = 'xsmall', colorId }) => {
  const avatarColor = colorId ? getAvatarColor(colorId) : undefined;
  const avatarColorClass =
    avatarColor && getAvatarUtilityClass(avatarColor[colorNameSymbol]);
  return {
    ...getHeightAndWidth(size, theme),
    ...(avatarColorClass && {
      // Duplicate the class name to add specificity so that this overrides the default colors set by Chip.
      [`&.${avatarColorClass}.${avatarColorClass}`]: {
        color: `${avatarColor.color}`,
        backgroundColor: `${avatarColor.backgroundColor}`,
      },
    }),
  };
});

export default Avatar;
